import React, { useState } from 'react';
import styled from 'styled-components';

import { DispensaryMenuBannerColors } from 'shared/constants';

import RichTextHtmlWrapper from 'shared/components/rich-text-html-wrapper';
import SvgChevronIcon from 'assets/chevron-icon';
import { tracker } from 'src/utils/analytics';
import useDispensary from 'src/dispensary/hooks/use-dispensary';

/**
 * An explicit, author-authored accordion decision. Supplying this opts the banner out of the
 * store-configured behavior entirely: both `dispensary.menuBannerTextBannerCollapsed` and the
 * content-length threshold below are ignored, because an author who has chosen "collapsed" wants
 * a collapsed banner whether or not the copy happens to clear that threshold.
 *
 * `initiallyCollapsed` is only the *starting* state -- a reader can still toggle it.
 */
export type MenuBannerAccordion = { enabled: boolean; initiallyCollapsed: boolean };

/**
 * Length above which store-configured banners collapse. Only consulted when no `accordion`
 * override is supplied.
 */
const STORE_COLLAPSE_CONTENT_LENGTH = 500;

export default function MenuBanner({
  htmlContent,
  color,
  className,
  accordion,
}: {
  htmlContent?: string;
  color: string;
  className?: string;
  accordion?: MenuBannerAccordion;
}): JSX.Element {
  const { dispensary } = useDispensary();

  const isContentLongAndCollapsed =
    (htmlContent?.length ?? 0) > STORE_COLLAPSE_CONTENT_LENGTH && (dispensary.menuBannerTextBannerCollapsed ?? true);

  const isCollapsible = accordion?.enabled ?? true;
  const startsCollapsed = accordion ? accordion.enabled && accordion.initiallyCollapsed : isContentLongAndCollapsed;

  const [isCollapsed, setIsCollapsed] = useState(startsCollapsed);

  // The reader's own toggle lives in state, so a changed authoring decision would otherwise be
  // invisible until remount -- in the CMS editor the panel writes new props to a mounted block,
  // so toggling the accordion controls would appear to do nothing.
  //
  // Gated on `accordion` so the store-configured path stays frozen at its mount-time state, as it
  // was before this prop existed. There, `startsCollapsed` derives from dispensary data, and a
  // mid-session refetch would otherwise discard a reader's own expand/collapse.
  const [appliedStartsCollapsed, setAppliedStartsCollapsed] = useState(startsCollapsed);
  if (accordion && appliedStartsCollapsed !== startsCollapsed) {
    setAppliedStartsCollapsed(startsCollapsed);
    setIsCollapsed(startsCollapsed);
  }

  const handleExpand = (): void => {
    setIsCollapsed(false);
    tracker.gaGTMClickEvent({
      eventCategory: `text banner impression`,
      eventAction: `expanded`,
    });
  };

  const handleCollapse = (): void => {
    setIsCollapsed(true);
    tracker.gaGTMClickEvent({
      eventCategory: `text banner impression`,
      eventAction: `collapse`,
    });
  };

  return (
    <Container
      className={className}
      color={color || 'green'}
      isLongHtmlContent={isCollapsed}
      data-cy='menu-banner'
      data-test='menu-banner'
      data-testid='menu-banner'
      onClick={isCollapsed ? handleExpand : undefined}
    >
      <StyledRichTextWrapper isLongHtmlContent={isCollapsed} color={color || 'green'}>
        <RichTextHtmlWrapper html={htmlContent} />
      </StyledRichTextWrapper>
      {isCollapsible && (
        <StyledChevronWrapper isLongHtmlContent={isCollapsed} onClick={isCollapsed ? handleExpand : handleCollapse}>
          <SvgChevronIcon fill={color} width='var(--dcm-banner-chevron-size)' />
        </StyledChevronWrapper>
      )}
    </Container>
  );
}

const Container = styled.div<{ color: string; isLongHtmlContent?: boolean }>`
  background: ${(props) => DispensaryMenuBannerColors[props.color].background};
  border-radius: var(--dcm-banner-border-radius);
  color: ${(props) => DispensaryMenuBannerColors[props.color].color};
  cursor: default;
  display: flex;
  flex-direction: row;
  font-size: var(--dcm-banner-font-size);
  justify-content: space-between;
  line-height: var(--dcm-banner-line-height);
  margin: var(--dcm-banner-margin);
  max-width: var(--dcm-banner-max-width);
  padding: var(--dcm-banner-padding);
  word-break: break-word;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    margin: var(--dcm-banner-margin-sm);
    padding: var(--dcm-banner-padding);
  }

  strong {
    font-weight: bold;
  }

  em {
    font-style: italic;
  }

  a {
    color: var(--dcm-color-primary);
  }

  ${({ isLongHtmlContent }) =>
    isLongHtmlContent &&
    `
      height: var(--dcm-banner-collapsed-height);
      overflow: hidden;
      cursor: pointer;
    `}
`;

const StyledChevronWrapper = styled.div<{ isLongHtmlContent?: boolean }>`
  display: flex;
  height: min-content;
  width: 25px;

  ${({ isLongHtmlContent }) =>
    !isLongHtmlContent &&
    `
      cursor: pointer;
      svg {
       transform: rotate(180deg);
      }
    `}
`;

const StyledRichTextWrapper = styled.div<{ isLongHtmlContent?: boolean; color: string }>`
  ${({ isLongHtmlContent, color }) =>
    isLongHtmlContent &&
    `
      height: var(--dcm-banner-mask-height);
      -webkit-mask-image: linear-gradient(180deg, ${DispensaryMenuBannerColors[color].background} 60%, transparent);

      &:hover {
        -webkit-mask-image: linear-gradient(180deg, ${DispensaryMenuBannerColors[color].background} 100%, transparent);
      }
    `}
`;
